import { ReactNode, useContext, useState } from "react"
import { AppActionContext } from "../AppState.ts"
import { useCanvasPainter } from "../hooks/useCanvasPainter.tsx"
import { useEventListener } from "../hooks/useEventListener.ts"
import { Rect } from "../Rect.tsx"
import { doubleStroke } from "./GraphicsUtilities.tsx"
import { Timeline } from "./Timeline.ts"

export function Keyframes(props: {
  timeline: Timeline
}): ReactNode {
  const { timeline } = props

  const timelineDuration = timeline.duration
  return timeline.keyframes.map(kf => <Keyframe
    key={kf.time}
    time={kf.time}
    timelineDuration={timelineDuration} />)
}

function Keyframe(props: {
  time: number
  timelineDuration: number
}): ReactNode {
  const { time, timelineDuration } = props
  const xFraction = time / timelineDuration

  // This zero-size wrapper div lets me position the keyframe graphic by its center.
  return <div
    style={{
      position: 'absolute',
      top: '22.5px',
      left: `${xFraction * 100}%`,
      width: 0,
      height: 0,

      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',

      cursor: 'pointer',
    }}>
    <KeyframeGraphic time={time} />
  </div>
}

export const KeyframeGraphic = function KeyframeGraphic(props: {
  time: number
}): ReactNode {
  const { time } = props
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)

  const sendAppAction = useContext(AppActionContext)

  useEventListener('click', canvas, event => {
    event.stopPropagation()
    event.preventDefault()
    sendAppAction({
      type: 'setPlayheadTime',
      time,
    })
  })

  // I don't allow dragging keyframes, but I also don't let a pointerdown propagate and turn into a playhead drag.
  useEventListener('pointerdown', canvas, event => {
    event.stopPropagation()
    event.preventDefault()
  })

  const length = 15
  const lineWidth = 1

  useCanvasPainter(canvas, { alpha: true }, gc => {
    gc.lineCap = 'round'
    gc.lineJoin = 'miter'
    gc.miterLimit = 100

    const rect = new Rect(
      0, 0,
      length, length
    ).insetBy(2 * lineWidth)

    gc.moveTo(...rect.topMiddle.tuple)
    gc.lineTo(...rect.middleRight.tuple)
    gc.lineTo(...rect.bottomMiddle.tuple)
    gc.lineTo(...rect.middleLeft.tuple)
    gc.closePath()

    gc.fillStyle = 'white'
    gc.fill()

    doubleStroke(gc, lineWidth)
  }, [])

  return <canvas
    ref={setCanvas}
    style={{
      width: `${length}px`,
      height: `${length}px`,
    }} />
}
